import { hasSteve, MAX_FISH, peelFish, type Fish, type FishSpot } from '../components/april';
import type { Locale } from '../i18n/strings';
import { MAX_ENTRIES, MAX_ENTRY_LENGTH, sanitizeEntryText, sanitizeKikoo } from '../lib/sanitize';
import type {
  GuestbookEntry,
  LogEntry,
  LogEntryData,
  MixState,
  PresetName,
  SimpleLogKey,
  Skin,
  SkyblogTheme,
  SteveState,
  VariantLogKey,
} from './types';
import {
  DEFAULT_MIX,
  LOG_LIMIT,
  MAX_SEEN,
  MIX_RANGES,
  POST_COOLDOWN_MS,
  PRESETS,
} from './types';

/** Effets ponctuels du bac. La valeur portée est un jeton, pas un booléen. */
export type FxName = 'feedFx' | 'praiseFx' | 'bubbleFx';

export type Action =
  | { type: 'set-skin'; skin: Skin }
  | { type: 'set-lang'; lang: Locale }
  | { type: 'set-sb-theme'; theme: SkyblogTheme }
  | { type: 'set-kikoo'; name: string }
  | { type: 'stick-fish'; spot: FishSpot }
  | { type: 'peel-fish'; id: number }
  | { type: 'bump-seen' }
  | { type: 'set-seen'; seen: number }
  | { type: 'feed'; variant: number }
  | { type: 'praise'; variant: number }
  | { type: 'bubbles'; count: number }
  | { type: 'pad-hit'; variant: number }
  | { type: 'log'; key: SimpleLogKey }
  | { type: 'fx-end'; fx: FxName; token: number }
  | { type: 'set-playing'; playing: boolean }
  | { type: 'audio-missing' }
  | { type: 'set-mix'; patch: Partial<MixState> }
  | { type: 'preset'; name: PresetName }
  | { type: 'toggle-wobble' }
  | { type: 'toggle-hold' }
  | { type: 'toggle-follower' }
  | { type: 'set-draft'; draft: string }
  | { type: 'post-entry'; at: number }
  | { type: 'open-egg' }
  | { type: 'close-egg' }
  | { type: 'tick'; clock: string };

/** Ramène une valeur de curseur dans ses bornes ; `NaN` retombe sur la valeur courante. */
function clampMix<K extends keyof typeof MIX_RANGES>(key: K, value: number, current: number): number {
  if (!Number.isFinite(value)) return current;
  const { min, max } = MIX_RANGES[key];
  return Math.min(max, Math.max(min, value));
}

function withLog(state: SteveState, entry: LogEntryData): SteveState {
  const line: LogEntry = { ...entry, id: state.nextId };
  return {
    ...state,
    log: [line, ...state.log].slice(0, LOG_LIMIT),
    nextId: state.nextId + 1,
  };
}

function variantLog(state: SteveState, key: VariantLogKey, variant: number): SteveState {
  return withLog(state, { kind: 'variant', key, variant: Math.max(0, Math.trunc(variant)) });
}

/** Déclenche un effet : nouveau jeton, donc nouvelle animation même en rafale. */
function fire(state: SteveState, fx: FxName): SteveState {
  return { ...state, [fx]: state.nextId, nextId: state.nextId + 1 };
}

function applyMix(state: SteveState, patch: Partial<MixState>): SteveState {
  const mix = state.mix;
  return {
    ...state,
    mix: {
      rate: patch.rate === undefined ? mix.rate : clampMix('rate', patch.rate, mix.rate),
      cutoff: patch.cutoff === undefined ? mix.cutoff : clampMix('cutoff', patch.cutoff, mix.cutoff),
      echo: patch.echo === undefined ? mix.echo : clampMix('echo', patch.echo, mix.echo),
      vol: patch.vol === undefined ? mix.vol : clampMix('vol', patch.vol, mix.vol),
      wobble: patch.wobble === undefined ? mix.wobble : patch.wobble,
    },
  };
}

const PRESET_LOG: Record<PresetName, SimpleLogKey> = {
  normal: 'preset_normal',
  deep: 'preset_deep',
  fast: 'preset_fast',
  choir: 'preset_choir',
};

export function initialState(
  skin: Skin,
  lang: Locale,
  seen: number,
  sbTheme: SkyblogTheme = 'pink',
  kikoo = '',
  fish: readonly Fish[] = [],
): SteveState {
  // Les trois lignes de démarrage s'affichent dans l'ordre du design :
  // « AquariumOS démarré. », puis la pompe, puis Steve qui marche. Les lignes
  // écrites ensuite s'empilent au-dessus, la plus récente en tête.
  const boot: LogEntry[] = [0, 1, 2].map((variant, index) => ({
    id: index,
    kind: 'variant',
    key: 'boot',
    variant,
  }));
  const guestbook: GuestbookEntry[] = [0, 1, 2].map((index) => ({
    id: 100 + index,
    kind: 'seed',
    index,
  }));

  return {
    skin,
    lang,
    sbTheme,
    kikoo: sanitizeKikoo(kikoo),
    fish: fish.slice(0, MAX_FISH),
    seen: Math.min(MAX_SEEN, Math.max(0, Math.trunc(seen))),
    meals: 0,
    playing: false,
    audioMissing: false,
    mix: DEFAULT_MIX,
    hold: false,
    followCursor: true,
    log: boot,
    guestbook,
    draft: '',
    feedFx: 0,
    praiseFx: 0,
    bubbleFx: 0,
    egg: false,
    clock: '',
    lastPostAt: 0,
    nextId: 1000,
  };
}

export function reducer(state: SteveState, action: Action): SteveState {
  switch (action.type) {
    case 'set-skin':
      return state.skin === action.skin ? state : { ...state, skin: action.skin };

    case 'set-lang':
      return state.lang === action.lang ? state : { ...state, lang: action.lang };

    case 'set-sb-theme':
      // Le changement laisse une trace dans le journal partagé : c'est la seule
      // action du skyblog que les autres skins peuvent lire.
      return state.sbTheme === action.theme
        ? state
        : withLog({ ...state, sbTheme: action.theme }, { kind: 'simple', key: 'sb_theme' });

    case 'set-kikoo': {
      // Le pseudo vient du générateur du skyblog mais il sort du skin : c'est
      // la signature du visiteur partout ailleurs. Nettoyé ici, une bonne fois,
      // plutôt qu'à chaque endroit qui l'affiche.
      const name = sanitizeKikoo(action.name);
      if (name === state.kikoo) return state;
      return withLog(
        { ...state, kikoo: name },
        { kind: 'simple', key: name === '' ? 'kikoo_off' : 'kikoo' },
      );
    }

    case 'stick-fish': {
      /*
       * Le placement vient du fournisseur, qui a le hasard ; les deux règles du
       * mur sont vérifiées ici, où l'état vit. Une action fabriquée à la main ne
       * peut donc ni dépasser la borne, ni coller un second Steve — il n'y en a
       * qu'un, c'est le principe.
       */
      if (state.fish.length >= MAX_FISH) return state;
      const spot = action.spot;
      const fish: Fish = {
        ...spot,
        steve: spot.steve && !hasSteve(state.fish),
        id: state.nextId,
      };
      return { ...state, fish: [...state.fish, fish], nextId: state.nextId + 1 };
    }

    case 'peel-fish': {
      const fish = peelFish(state.fish, action.id);
      return fish.length === state.fish.length ? state : { ...state, fish };
    }

    case 'bump-seen':
      return state.seen >= MAX_SEEN ? state : { ...state, seen: state.seen + 1 };

    case 'set-seen':
      return { ...state, seen: Math.min(MAX_SEEN, Math.max(0, Math.trunc(action.seen))) };

    case 'feed': {
      const fed = { ...state, meals: Math.min(MAX_SEEN, state.meals + 1) };
      return variantLog(fire(fed, 'feedFx'), 'feed', action.variant);
    }

    case 'praise':
      return variantLog(fire(state, 'praiseFx'), 'praise', action.variant);

    case 'bubbles':
      return withLog(fire(state, 'bubbleFx'), {
        kind: 'bubbles',
        count: Math.max(0, Math.trunc(action.count)),
      });

    case 'pad-hit':
      return variantLog(state, 'pad', action.variant);

    case 'log':
      return withLog(state, { kind: 'simple', key: action.key });

    case 'fx-end':
      // Le jeton évite qu'un timer périmé éteigne l'effet suivant.
      return state[action.fx] === action.token ? { ...state, [action.fx]: 0 } : state;

    case 'set-playing':
      // Une lecture qui démarre lave le drapeau « fichier absent » : le morceau
      // a pu échouer une fois (réseau) puis repartir.
      return {
        ...state,
        playing: action.playing,
        audioMissing: action.playing ? false : state.audioMissing,
      };

    case 'audio-missing':
      return { ...state, audioMissing: true, playing: false };

    case 'set-mix':
      return applyMix(state, action.patch);

    case 'preset':
      return withLog(applyMix(state, PRESETS[action.name]), {
        kind: 'simple',
        key: PRESET_LOG[action.name],
      });

    case 'toggle-wobble': {
      const wobble = !state.mix.wobble;
      return withLog(applyMix(state, { wobble }), {
        kind: 'simple',
        key: wobble ? 'wobble_on' : 'wobble_off',
      });
    }

    case 'toggle-follower':
      return { ...state, followCursor: !state.followCursor };

    case 'toggle-hold': {
      const hold = !state.hold;
      return withLog({ ...state, hold }, { kind: 'simple', key: hold ? 'hold_on' : 'hold_off' });
    }

    case 'set-draft':
      // Le champ est plafonné dès la saisie : un collage massif ne doit pas
      // faire grossir l'état ni ramer le rendu.
      return { ...state, draft: action.draft.slice(0, MAX_ENTRY_LENGTH * 2) };

    case 'post-entry': {
      const text = sanitizeEntryText(state.draft);
      if (text === '') return { ...state, draft: '' };
      if (action.at - state.lastPostAt < POST_COOLDOWN_MS) return state;

      const entry: GuestbookEntry = { id: state.nextId, kind: 'visitor', text };
      return withLog(
        {
          ...state,
          draft: '',
          lastPostAt: action.at,
          guestbook: [entry, ...state.guestbook].slice(0, MAX_ENTRIES),
          nextId: state.nextId + 1,
        },
        { kind: 'simple', key: 'guestbook' },
      );
    }

    case 'open-egg':
      return state.egg ? state : { ...state, egg: true };

    case 'close-egg':
      return state.egg ? { ...state, egg: false } : state;

    case 'tick':
      return state.clock === action.clock ? state : { ...state, clock: action.clock };
  }
}
